import { Button } from "@app/ui";

import { useI18n } from "../../lib/useI18n.js";
import { useSettingsStore } from "../../stores/index.js";
import type { ExcludableView, TopicExcludableView } from "../../stores/settings.store.js";
import { toggleExcluded } from "./exclusions.js";

const CALENDAR_VIEWS: ExcludableView[] = ["month", "today"];
const TOPIC_VIEWS: TopicExcludableView[] = ["month", "today", "priorities"];

/** Per-entity visibility toggles. Topics support Month / Today / Priorities;
 * calendars support Month / Today. Inverse of the persisted exclusion arrays
 * — a pressed pill means "shown", unpressed means the id is excluded there. */
export function ShowInViewsControl({ kind, id }: { kind: "topic" | "calendar"; id: string }) {
  const { t } = useI18n();
  const monthTopicIds = useSettingsStore((s) => s.monthExcludedTopicIds);
  const todayTopicIds = useSettingsStore((s) => s.todayExcludedTopicIds);
  const monthCalendarIds = useSettingsStore((s) => s.monthExcludedCalendarIds);
  const todayCalendarIds = useSettingsStore((s) => s.todayExcludedCalendarIds);
  const prioritiesTopicIds = useSettingsStore((s) => s.prioritiesExcludedTopicIds);
  const setExcludedTopics = useSettingsStore((s) => s.setViewExcludedTopics);
  const setExcludedCalendars = useSettingsStore((s) => s.setViewExcludedCalendars);

  const excludedFor = (view: TopicExcludableView): string[] => {
    if (kind === "topic") {
      if (view === "month") return monthTopicIds;
      if (view === "today") return todayTopicIds;
      return prioritiesTopicIds;
    }
    return view === "month" ? monthCalendarIds : todayCalendarIds;
  };

  function toggle(view: TopicExcludableView) {
    const next = toggleExcluded(excludedFor(view), id);
    if (kind === "topic") void setExcludedTopics(view, next);
    else void setExcludedCalendars(view as ExcludableView, next);
  }

  const views = kind === "topic" ? TOPIC_VIEWS : CALENDAR_VIEWS;
  const viewLabel = (view: TopicExcludableView) => t(`organize.showIn.${view}`);

  return (
    <div className="flex min-w-0 flex-wrap items-center justify-end gap-1.5">
      <span className="text-xs text-muted-foreground">{t("organize.showIn")}</span>
      {views.map((view) => {
        const shown = !excludedFor(view).includes(id);
        return (
          <Button
            key={view}
            type="button"
            size="sm"
            variant={shown ? "primary" : "outline"}
            aria-pressed={shown}
            onClick={() => toggle(view)}
            title={shown ? t("organize.showIn.shownTitle", { view: viewLabel(view) }) : t("organize.showIn.hiddenTitle", { view: viewLabel(view) })}
          >
            {viewLabel(view)}
          </Button>
        );
      })}
    </div>
  );
}
